import { PRIVATEPATHS } from '@core/enums/paths.enum';
import { Product } from '@core/models/product.model';
import { ISftpStaging } from '@core/models/sftp-transfer.model';
import { getSftpTransferThunk } from '@redux/features/sftp-integration.slice';
import { getProductsThunk } from '@redux/features/product.slice';
import { getSuppliersThunk } from '@redux/features/supplier.slice';
import { getWarehousesThunk } from '@redux/features/warehouse.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import BreadCrumb from 'Common/BreadCrumb';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import InboundAdminStagingForm, {
    applyPreviewHints,
    emptyInboundStaging,
    InboundStagingFormState,
    stagingFromApi,
    toStagingSavePayload,
} from 'components/integrations/InboundAdminStagingForm';
import { parseEpcisPreview } from 'components/integrations/epcis-preview.helpers';
import EpcisProductIdentityPreviewPanel from 'components/integrations/EpcisProductIdentityPreviewPanel';
import InboundValidationLayersPanel from 'components/integrations/InboundValidationLayersPanel';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import sftpIntegrationService from 'services/api/sftp-integration.service';
import { apiErrorToastMessage } from './sftp-user-messages';
import {
    canReprocessValidation,
    canRetryPostApprovalProcessing,
    formatApprovalLabel,
    formatStatusLabel,
    isSftpStagingEditable,
    isSftpTransferEditable,
    needsValidationAttention,
    sftpStatusBadgeClass,
} from './sftp-transfer-status.utils';
import { formatTransferDate, inferFormatSupported, isInboundValidationPassing, parseDscsaViolations, parseJsonStringList, parseL2StructuralFallback, parsePreviewSchemaWarnings, parseSemanticWarnings } from './sftp-transfer-detail.utils';
import SftpTransferTimeline, { ISftpStatusHistoryEntry } from './SftpTransferTimeline';

function stagingFromTransfer(
    staging?: ISftpStaging | null,
    previewJson?: string | null,
    products: Product[] = []
): InboundStagingFormState {
    return stagingFromApi(staging as Record<string, unknown> | null | undefined, previewJson, products);
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
            <span className="min-w-[140px] text-sm font-medium text-slate-500">{label}</span>
            <span className="text-sm text-slate-800">{value ?? '—'}</span>
        </div>
    );
}

const SftpTransferDetail: FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { selected, detailLoading } = useAppSelector((state: RootState) => state.sftpIntegrationState);
    const { warehouses } = useAppSelector((state: RootState) => state.warehouseState);
    const { suppliers } = useAppSelector((state: RootState) => state.supplierState);
    const { products } = useAppSelector((state: RootState) => state.productState);

    const [staging, setStaging] = useState<InboundStagingFormState>(emptyInboundStaging);
    const [actionLoading, setActionLoading] = useState(false);
    const [history, setHistory] = useState<ISftpStatusHistoryEntry[]>([]);

    const productList: Product[] = useMemo(() => (products?.content || []) as Product[], [products]);
    const isPreApprovalEditable = isSftpTransferEditable(selected?.status, selected?.approvalStatus);
    const isStagingEditable = isSftpStagingEditable(selected?.status, selected?.approvalStatus);
    const dscsaViolationList = useMemo(
        () => parseDscsaViolations(selected?.dscsaViolations),
        [selected?.dscsaViolations]
    );
    const schemaErrorList = useMemo(
        () => parseJsonStringList(selected?.schemaErrors),
        [selected?.schemaErrors]
    );
    const validationWarningList = useMemo(() => {
        const fromApi = parseJsonStringList(selected?.validationWarnings);
        if (fromApi.length > 0) return fromApi;
        return parseSemanticWarnings(selected?.previewJson);
    }, [selected?.validationWarnings, selected?.previewJson]);
    const schemaWarningList = useMemo(
        () => parsePreviewSchemaWarnings(selected?.previewJson),
        [selected?.previewJson]
    );
    const l2StructuralFallback = useMemo(
        () => parseL2StructuralFallback(selected?.previewJson),
        [selected?.previewJson]
    );
    const formatSupported = useMemo(
        () =>
            inferFormatSupported(
                selected?.formatOverview,
                selected?.detectedSchemaVersion,
                selected?.previewJson
            ),
        [selected?.formatOverview, selected?.detectedSchemaVersion, selected?.previewJson]
    );
    const canApprove =
        isPreApprovalEditable &&
        isInboundValidationPassing(selected?.schemaValid, selected?.dscsaCompliant);

    useEffect(() => {
        dispatch(getWarehousesThunk());
        dispatch(getSuppliersThunk());
        dispatch(getProductsThunk({ page: 0, productSize: 1000 }));
    }, [dispatch]);

    const loadTransfer = async (transferId: number) => {
        dispatch(getSftpTransferThunk(transferId));
        try {
            const res = await sftpIntegrationService.getHistory(transferId);
            setHistory(res.data?.statusHistory || []);
        } catch {
            setHistory([]);
        }
    };

    useEffect(() => {
        if (id) {
            loadTransfer(Number(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (selected) {
            const preview = selected.previewJson ?? selected.staging?.previewJson;
            let next = stagingFromTransfer(selected.staging, preview, productList);
            next = applyPreviewHints(next, preview, productList);
            setStaging(next);
        }
    }, [selected, productList]);

    const saveStaging = async () => {
        if (!id || !isStagingEditable) return;
        setActionLoading(true);
        try {
            await sftpIntegrationService.saveStaging(Number(id), toStagingSavePayload(staging));
            toast.success('Staging saved');
            await loadTransfer(Number(id));
        } catch (err) {
            toast.error(apiErrorToastMessage(err, 'Failed to save staging'));
        } finally {
            setActionLoading(false);
        }
    };

    const approve = async () => {
        if (!id || !isPreApprovalEditable) return;
        const previewJson = selected?.previewJson ?? selected?.staging?.previewJson;
        const summary = parseEpcisPreview(previewJson);
        if (previewJson?.trim() && summary && !summary.despatchAdviceHint) {
            toast.error('Shipment ID not found in preview — re-run validation before approve.');
            return;
        }
        const multiLine = staging.productLines.length > 1 || summary?.multiProduct;
        if (multiLine && !staging.purchaseOrderNumber.trim()) {
            toast.error('Purchase order number is required for multi-product inbound');
            return;
        }
        if (staging.productLines.length > 0) {
            for (const line of staging.productLines) {
                if (!line.productId) {
                    toast.error('Select a catalog product for each EPCIS line');
                    return;
                }
                const qty = Number(line.quantity) || 0;
                const serialCount = line.serialNumbers.filter((s) => s.trim()).length;
                if (serialCount > 0 && qty !== serialCount) {
                    toast.error(`Quantity must match serial count for ${line.epcisProductLineKey}`);
                    return;
                }
            }
        } else {
            const qty = Number(staging.quantity) || 0;
            const serialCount = staging.serialNumbers.filter((s) => s.trim()).length;
            if (serialCount > 0 && qty !== serialCount) {
                toast.error('Quantity must match the number of serial numbers');
                return;
            }
        }
        setActionLoading(true);
        try {
            await sftpIntegrationService.approve(Number(id), staging.comment);
            const identityKeys =
                summary?.productLines?.filter((l) => l.sgtinItemKey).length ??
                (summary?.sgtinItemKey ? 1 : 0);
            toast.success(
                identityKeys > 0
                    ? `Approved — product identity (GTIN/prefix/itemRef) applied from EPCIS for ${identityKeys} line(s)`
                    : 'Approved — import processing started'
            );
            await loadTransfer(Number(id));
        } catch (err) {
            toast.error(apiErrorToastMessage(err, 'Failed to approve transfer'));
        } finally {
            setActionLoading(false);
        }
    };

    const reject = async () => {
        if (!id || !isPreApprovalEditable) return;
        setActionLoading(true);
        try {
            await sftpIntegrationService.reject(Number(id), staging.comment);
            toast.info('Transfer rejected');
            navigate(PRIVATEPATHS.SftpIntegrations);
        } catch (err) {
            toast.error(apiErrorToastMessage(err, 'Failed to reject transfer'));
        } finally {
            setActionLoading(false);
        }
    };

    const download = async () => {
        if (!id) return;
        try {
            const res = await sftpIntegrationService.download(Number(id));
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = selected?.remoteFileName || `sftp-${id}`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(apiErrorToastMessage(err, 'Failed to download file'));
        }
    };

    const reprocessValidation = async () => {
        if (!id) return;
        setActionLoading(true);
        try {
            await sftpIntegrationService.reprocessValidation(Number(id));
            toast.success('Validation completed. Refreshing transfer…');
            await loadTransfer(Number(id));
        } catch (err) {
            toast.error(apiErrorToastMessage(err, 'Failed to re-run validation'));
        } finally {
            setActionLoading(false);
        }
    };

    const retryProcessing = async () => {
        if (!id) return;
        setActionLoading(true);
        try {
            const res = await sftpIntegrationService.retryProcessing(Number(id));
            const updated = res.data?.sftpTransfer;
            if (updated?.status === 'PROCESSED') {
                toast.success('Processing completed successfully');
            } else if (updated?.errorMessage) {
                toast.warning(updated.errorMessage);
            } else {
                toast.warning('Processing did not complete — check status below');
            }
            await loadTransfer(Number(id));
        } catch (err) {
            toast.error(apiErrorToastMessage(err, 'Failed to retry processing'));
        } finally {
            setActionLoading(false);
        }
    };

    const showReprocess = canReprocessValidation(selected?.status);
    const showRetryProcessing = canRetryPostApprovalProcessing(
        selected?.status,
        selected?.approvalStatus
    );
    const disabled = actionLoading || !isStagingEditable;

    return (
        <>
            <BreadCrumb pageTitle={`SFTP Transfer #${id}`} />
            <section className="py-5">
                <div className="flex flex-col gap-4 bg-[#fff] p-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <h3 className="font-extrabold text-[#bd902e]">Transfer detail</h3>
                        <Link
                            to={PRIVATEPATHS.SftpIntegrations}
                            className="text-sm font-semibold text-slate-600 hover:text-[#bd902e] hover:underline"
                        >
                            Back to list
                        </Link>
                    </div>

                    {detailLoading && (
                        <p className="text-sm text-slate-500">Loading transfer…</p>
                    )}

                    {!detailLoading && selected && (
                        <>
                            {showReprocess && (
                                <div
                                    className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                                    role="status"
                                >
                                    {needsValidationAttention(selected.status) ? (
                                        <p>
                                            <strong>Validation did not finish</strong> after download (common with the
                                            5-minute poll). Schema is empty until validation succeeds. Click{' '}
                                            <strong>Re-run validation</strong> below — no need to re-upload the file.
                                        </p>
                                    ) : (
                                        <p>
                                            EPCIS validation failed or did not complete. Review the error below, fix the
                                            file if needed, then click <strong>Re-run validation</strong>.
                                        </p>
                                    )}
                                    {selected.errorMessage && (
                                        <p className="mt-2 font-mono text-xs text-red-800">{selected.errorMessage}</p>
                                    )}
                                    {selected.validationSummary && (
                                        <p className="mt-2 text-sm text-amber-900">{selected.validationSummary}</p>
                                    )}
                                    {schemaErrorList.length > 0 && (
                                        <details className="mt-2">
                                            <summary className="cursor-pointer text-xs font-medium text-red-900">
                                                Schema details ({schemaErrorList.length})
                                            </summary>
                                            <ul className="mt-2 list-inside list-disc text-xs text-red-800">
                                                {schemaErrorList.map((line, i) => (
                                                    <li key={i} className="whitespace-pre-wrap font-mono">
                                                        {line}
                                                    </li>
                                                ))}
                                            </ul>
                                        </details>
                                    )}
                                </div>
                            )}
                            {selected.status === 'ARCHIVED_REMOTE' && isPreApprovalEditable && (
                                <div
                                    className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900"
                                    role="status"
                                >
                                    The file was moved from <strong>/upload</strong> to the SFTP{' '}
                                    <strong>archive</strong> folder after download so it is not picked up again. You can
                                    still fill staging and approve here.
                                </div>
                            )}
                            {showRetryProcessing && (
                                <div
                                    className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
                                    role="status"
                                >
                                    <p>
                                        <strong>Post-approval processing failed.</strong> Staging was saved and the
                                        transfer was approved, but batch/EPCIS/DSCSA processing did not finish. Update
                                        the fields below (e.g. expiry date), click <strong>Save staging</strong>, then
                                        click <strong>Retry processing</strong> — no need to re-upload the file.
                                    </p>
                                </div>
                            )}
                            {!isStagingEditable && !showReprocess && !showRetryProcessing && (
                                <div
                                    className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                                    role="status"
                                >
                                    <p>
                                        This transfer is <strong>{formatStatusLabel(selected.status)}</strong> and
                                        cannot be modified. View-only mode.
                                    </p>
                                </div>
                            )}

                            {(selected.schemaValid === false || selected.dscsaCompliant === false) &&
                                (selected.errorMessage ||
                                    schemaErrorList.length > 0 ||
                                    validationWarningList.length > 0 ||
                                    dscsaViolationList.length > 0) && (
                                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                                    <h4 className="mb-2 text-sm font-semibold text-red-900">Errors</h4>
                                    {selected.validationSummary && selected.schemaValid === false && (
                                        <p className="mb-2 text-sm font-medium text-red-900">
                                            {selected.validationSummary}
                                        </p>
                                    )}
                                    {selected.errorMessage && (
                                        <p className="mb-2 whitespace-pre-wrap font-mono text-xs text-red-800">
                                            {selected.errorMessage}
                                        </p>
                                    )}
                                    {schemaErrorList.length > 0 && (
                                        <div className="mb-2">
                                            <p className="text-xs font-medium text-red-900">Schema</p>
                                            <details className="mt-1">
                                                <summary className="cursor-pointer text-xs text-red-800">
                                                    Show {schemaErrorList.length} detail
                                                    {schemaErrorList.length === 1 ? '' : 's'}
                                                </summary>
                                                <ul className="mt-2 list-inside list-disc font-mono text-xs text-red-800">
                                                    {schemaErrorList.map((line, i) => (
                                                        <li key={i} className="whitespace-pre-wrap">
                                                            {line}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </details>
                                        </div>
                                    )}
                                    {dscsaViolationList.length > 0 && (
                                        <div>
                                            <p className="text-xs font-medium text-red-900">DSCSA violations</p>
                                            <ul className="mt-1 list-inside list-disc text-xs text-red-800">
                                                {dscsaViolationList.map((v) => (
                                                    <li key={v}>{v}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}

                            <InboundValidationLayersPanel
                                formatSupported={formatSupported}
                                schemaValid={selected.schemaValid}
                                dscsaCompliant={selected.dscsaCompliant}
                                semanticWarningCount={validationWarningList.length}
                                schemaWarningCount={schemaWarningList.length}
                                l2StructuralFallbackUsed={l2StructuralFallback}
                            />

                            {staging.productLines.length === 1 && staging.productLines[0].productId ? (
                                <EpcisProductIdentityPreviewPanel
                                    productId={staging.productLines[0].productId}
                                    sgtinItemKey={
                                        parseEpcisPreview(selected?.previewJson ?? selected?.staging?.previewJson)?.productLines?.[0]?.sgtinItemKey ??
                                        parseEpcisPreview(selected?.previewJson ?? selected?.staging?.previewJson)?.sgtinItemKey
                                    }
                                />
                            ) : null}

                            {validationWarningList.length > 0 && (
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                                    <h4 className="mb-2 text-sm font-semibold text-amber-900">Validation notes</h4>
                                    <ul className="list-inside list-disc text-sm text-amber-900">
                                        {validationWarningList.map((w) => (
                                            <li key={w}>{w}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {isInboundValidationPassing(selected.schemaValid, selected.dscsaCompliant) &&
                                selected.validationSummary && (
                                <div
                                    className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900"
                                    role="status"
                                >
                                    {selected.validationSummary}
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                    <SftpTransferTimeline
                                        history={history}
                                        currentStatus={selected.status}
                                    />
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                    <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600">
                                        Transfer information
                                    </h4>
                                    <div className="grid grid-cols-1 gap-3">
                                        <InfoRow
                                            label="Status"
                                            value={
                                                <span
                                                    className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${sftpStatusBadgeClass(selected.status)}`}
                                                >
                                                    {formatStatusLabel(selected.status)}
                                                </span>
                                            }
                                        />
                                        <InfoRow
                                            label="Approval"
                                            value={formatApprovalLabel(selected.approvalStatus)}
                                        />
                                        <InfoRow
                                            label="Partner"
                                            value={
                                                selected.partnerName
                                                    ? `${selected.partnerName} (${selected.partnerCode})`
                                                    : selected.partnerCode
                                            }
                                        />
                                        <InfoRow label="File" value={selected.remoteFileName} />
                                        <InfoRow
                                            label="Schema"
                                            value={
                                                selected.schemaValid === true ? (
                                                    <span className="font-medium text-green-700">
                                                        Valid
                                                        {selected.validationSummary
                                                            ? ` — ${selected.validationSummary}`
                                                            : ''}
                                                    </span>
                                                ) : selected.schemaValid === false ? (
                                                    <span className="font-medium text-red-600">
                                                        Invalid
                                                        {selected.validationSummary
                                                            ? ` — ${selected.validationSummary}`
                                                            : ''}
                                                    </span>
                                                ) : (
                                                    '—'
                                                )
                                            }
                                        />
                                        <InfoRow label="EPCIS version" value={selected.detectedSchemaVersion || '—'} />
                                        <InfoRow
                                            label="DSCSA"
                                            value={
                                                selected.dscsaCompliant === true ? (
                                                    <span className="font-medium text-green-700">Compliant</span>
                                                ) : selected.dscsaCompliant === false ? (
                                                    <span className="font-medium text-red-600">Not compliant</span>
                                                ) : (
                                                    '—'
                                                )
                                            }
                                        />
                                        <InfoRow label="Created" value={formatTransferDate(selected.createdAt)} />
                                        {selected.processedAt && (
                                            <InfoRow
                                                label="Processed"
                                                value={formatTransferDate(selected.processedAt)}
                                            />
                                        )}
                                        {selected.batchId ? (
                                            <InfoRow
                                                label="Batch"
                                                value={
                                                    <Link
                                                        to={`${PRIVATEPATHS.Batch}/${selected.batchId}`}
                                                        className="font-medium text-blue-700 hover:underline"
                                                    >
                                                        Batch #{selected.batchId}
                                                    </Link>
                                                }
                                            />
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={download}
                                    disabled={selected.fileDownloadable === false}
                                    className="inline-flex items-center rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Download file
                                </button>
                                {showReprocess && (
                                    <ButtonBase
                                        innerText={actionLoading ? 'Validating…' : 'Re-run validation'}
                                        onClick={reprocessValidation}
                                        disabled={actionLoading}
                                    />
                                )}
                                {showRetryProcessing && (
                                    <ButtonBase
                                        innerText={actionLoading ? 'Retrying…' : 'Retry processing'}
                                        onClick={retryProcessing}
                                        disabled={actionLoading}
                                    />
                                )}
                                {isPreApprovalEditable && selected?.schemaValid === false && (
                                    <p className="w-full text-sm text-red-700">
                                        Approve is blocked until validation passes (schema and semantic if strict).
                                    </p>
                                )}
                                {isPreApprovalEditable && selected?.schemaValid !== false && selected?.dscsaCompliant === false && (
                                    <p className="w-full text-sm text-red-700">
                                        Approve is blocked — DSCSA R1.3 validation failed. Fix the file or reject.
                                    </p>
                                )}
                                {canApprove && (
                                    <ButtonBase
                                        innerText={actionLoading ? 'Approving…' : 'Approve'}
                                        onClick={approve}
                                        disabled={actionLoading}
                                    />
                                )}
                                {isPreApprovalEditable && (
                                    <button
                                        type="button"
                                        onClick={reject}
                                        disabled={actionLoading}
                                        className="inline-flex items-center rounded border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                                    >
                                        Reject
                                    </button>
                                )}
                            </div>

                            <div>
                                <h4 className="mb-3 font-semibold text-slate-800">Admin staging</h4>
                                <InboundAdminStagingForm
                                    staging={staging}
                                    setStaging={setStaging}
                                    disabled={disabled}
                                    suppliers={suppliers || []}
                                    products={productList}
                                    warehouses={warehouses || []}
                                    validationPreviewJson={
                                        selected?.previewJson ?? selected?.staging?.previewJson
                                    }
                                />
                                {isStagingEditable && (
                                    <div className="mt-4">
                                        <ButtonBase
                                            innerText={actionLoading ? 'Saving…' : 'Save staging'}
                                            onClick={saveStaging}
                                            disabled={actionLoading}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default SftpTransferDetail;
